import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { Close as CloseIcon, InfoOutlined as InfoIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { HubConnectionState } from "@microsoft/signalr";
import { getSharedConnection } from "../signalrConnection";
import { useAppSelector } from "../../hooks/sharedHooks";
import { NotificationsState, AppNotification } from "../models/notificationModels";
import useTranslation from "../../hooks/useTranslation";
import { dt } from "../../themes/redesignTokens";

/* ---- design tokens ---- */
const AUTO_DISMISS_MS = 5000;
const MAX_TOASTS = 3;
const INFO_ACCENT = "#3B82F6";
const TOAST_HEIGHT = 64;
const TOAST_GAP = 10;
/** Start below ActionToast area so they don't overlap */
const TOP_OFFSET = 80;

interface ToastItem {
  key: number;
  notification: AppNotification;
}

/* ---- Inject keyframes once (shared pattern with ActionToast) ---- */
let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  const id = "notification-toast-keyframes";
  if (document.getElementById(id)) { keyframesInjected = true; return; }
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes ntToastSlideIn {
      0% { transform: translateX(120%); opacity: 0; }
      100% { transform: translateX(0); opacity: 1; }
    }
    @keyframes ntToastSlideOut {
      0% { transform: translateX(0); opacity: 1; }
      100% { transform: translateX(120%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
  keyframesInjected = true;
}

/* ---- Single notification toast renderer ---- */
interface NotificationToastItemProps {
  toast: ToastItem;
  index: number;
  onDismiss: (key: number) => void;
  onClick: (toast: ToastItem) => void;
}

const NotificationToastItem: React.FC<NotificationToastItemProps> = ({
  toast,
  index,
  onDismiss,
  onClick,
}) => {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!exiting) return;
    const exitTimer = setTimeout(() => onDismiss(toast.key), 250);
    return () => clearTimeout(exitTimer);
  }, [exiting, toast.key, onDismiss]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExiting(true);
  }, []);

  const topPos = TOP_OFFSET + index * (TOAST_HEIGHT + TOAST_GAP);

  return (
    <Box
      data-testid="notification-toast"
      onClick={() => onClick(toast)}
      sx={{
        position: "fixed",
        top: `${topPos}px`,
        right: "16px",
        zIndex: 1500 + index,
        animation: exiting
          ? "ntToastSlideOut 0.25s ease forwards"
          : "ntToastSlideIn 0.3s ease",
        transition: "top 0.3s ease",
        pointerEvents: "auto",
        cursor: toast.notification.linkUrl ? "pointer" : "default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          backgroundColor: "#FFFFFF",
          borderRadius: "10px",
          borderLeft: `3px solid ${INFO_ACCENT}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          minWidth: "300px",
          maxWidth: "420px",
        }}
      >
        {/* Icon */}
        <Box sx={{ display: "flex", flexShrink: 0, mt: "1px" }}>
          <InfoIcon sx={{ fontSize: 16, color: INFO_ACCENT }} />
        </Box>

        {/* Title + Body */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              fontFamily: dt.font,
              color: "#0C1117",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: "1.4",
            }}
          >
            {toast.notification.title}
          </Box>
          <Box
            sx={{
              fontSize: "12px",
              fontFamily: dt.font,
              color: dt.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: "2px",
              lineHeight: "1.4",
            }}
          >
            {toast.notification.message}
          </Box>
        </Box>

        {/* Close button */}
        <Box
          onClick={handleClose}
          sx={{
            display: "flex",
            cursor: "pointer",
            flexShrink: 0,
            opacity: 0.5,
            transition: "opacity 0.15s ease",
            mt: "1px",
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16, color: "#8C9099" }} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Global toast component that shows a styled notification when a new
 * ReceiveNotification SignalR event arrives.
 *
 * Mount once in Layout.tsx so it is always present regardless of page.
 * Suppresses toasts when the notification panel is already open.
 *
 * Visually matches ActionToast (same border-radius, shadow, font, slide
 * animation) but uses a blue info accent and 2-line title+body layout.
 * Stacks BELOW ActionToast area (starts at top: 80px).
 */
const NotificationToast: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation("common");
  const { isPanelOpen } = useAppSelector<NotificationsState>(
    (state: any) => state.notifications
  );

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const keyCounter = useRef(0);
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  useEffect(() => { injectKeyframes(); }, []);

  const addToast = useCallback((notification: AppNotification) => {
    // Don't show toast if notification panel is open
    if (isPanelOpenRef.current) return;

    const key = keyCounter.current++;
    setToasts((prev) => {
      const next = [...prev, { key, notification }];
      // Keep max 3 toasts — remove oldest if over limit
      if (next.length > MAX_TOASTS) {
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    });
  }, []);

  const removeToast = useCallback((key: number) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const handleClick = useCallback(
    (toast: ToastItem) => {
      removeToast(toast.key);
      if (toast.notification.linkUrl) {
        navigate(toast.notification.linkUrl);
      }
    },
    [navigate, removeToast]
  );

  // Subscribe to ReceiveNotification on the shared SignalR connection
  useEffect(() => {
    const handler = (notification: AppNotification) => {
      addToast(notification);
    };

    // Try to subscribe immediately
    const connection = getSharedConnection();
    if (connection && connection.state === HubConnectionState.Connected) {
      connection.on("ReceiveNotification", handler);
    }

    // Poll for connection availability (race condition: toast mounts
    // before useNotificationHub finishes connecting)
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let subscribed = false;

    if (!connection || connection.state !== HubConnectionState.Connected) {
      pollInterval = setInterval(() => {
        const conn = getSharedConnection();
        if (conn && !subscribed) {
          conn.on("ReceiveNotification", handler);
          subscribed = true;
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, 500);
    } else {
      subscribed = true;
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      const conn = getSharedConnection();
      if (conn) {
        conn.off("ReceiveNotification", handler);
      }
    };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <>
      {toasts.map((toast, index) => (
        <NotificationToastItem
          key={toast.key}
          toast={toast}
          index={index}
          onDismiss={removeToast}
          onClick={handleClick}
        />
      ))}
    </>
  );
};

export default NotificationToast;
